import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { TimezoneService } from '../../../../../services/date/timezone.service';
import { DateService } from '../../../../../services/date/date.service';
import { TradingAccount } from '../../../../../domain/account.domain';

import { OrderEntryRestService } from '../order-entry-rest.service';
import {
  EquityFormModel,
  OptionFormModel,
  OrderDetailsParameters,
  OrderEntryChangeFormModel
} from '../../domain/order-entry.domain';
import {
  Order,
  OrderCapture,
  OrderItem,
  OrderRequest,
  OrderTerms
} from '../../../domain/order.domain';
import {
  EquityExtendedTerms,
  EquityOrderAction,
  OptionExtendedTerms,
  OptionOrderAction,
  OrderConditions,
  OrderStatusType,
  PriceType
} from '../../../domain';
import { OrderEntryUtilityService } from '../order-entry-utility.service';
import { SimpleChangeOrderForm } from '../../domain/simple-change-order-form';
import { ChangeOrderFormContainer } from '../../domain/change-order-form-container';

@Injectable()
export class ChangeOrderFormService {
  private readonly orderEntryRestService = inject(OrderEntryRestService);
  private readonly timezoneService = inject(TimezoneService);

  fetchChangeOrderDetails(params: OrderDetailsParameters): Observable<OrderCapture> {
    return this.orderEntryRestService.fetchOrderDetails(params, false);
  }

  validateTradingOrder(order: Order<OrderTerms>): Observable<OrderCapture> {
    return this.validateOrder(order);
  }

  validateOrderUpdate(order: OrderRequest, params: OrderDetailsParameters): Observable<OrderCapture> {
    return this.orderEntryRestService.validateUpdateOrder(order, params);
  }

  commitOrderUpdate(order: OrderRequest,
                    conditions: OrderConditions,
                    params: OrderDetailsParameters): Observable<OrderCapture> {
    if (conditions !== undefined && conditions.conditions !== undefined
      && Object.keys(conditions.conditions).length !== 0) {
      order.acknowledgements = {
        conditions: conditions.conditions
      };

      OrderEntryUtilityService.acknowledgeConditions(order.acknowledgements.conditions, conditions);
    }
    return this.orderEntryRestService.commitUpdateOrder(order, params);
  }

  getAccountInfo(selectedTradingAccount: TradingAccount): { accountId: string } {
    return {
      accountId: selectedTradingAccount.id
    };
  }

  getTodaysDate(): Date {
    return DateService.stripTime(this.timezoneService.getCurrentDateInServerTimezone());
  }

  getOrderItemExtendedTerms(securityFormValue: SimpleChangeOrderForm<EquityFormModel | OptionFormModel>,
                            extendedTerms: EquityExtendedTerms | OptionExtendedTerms,
                            orderAction: EquityOrderAction | OptionOrderAction): EquityExtendedTerms | OptionExtendedTerms {
    extendedTerms.orderAction = orderAction;
    OrderEntryUtilityService.setEquityPrice(extendedTerms, securityFormValue.orderPrice, securityFormValue.orderPriceAmount);
    extendedTerms.orderType = securityFormValue.orderPrice as PriceType;
    extendedTerms.goodTill = securityFormValue.orderGoodTil;
    if (extendedTerms.goodTill === 'DATE') {
      extendedTerms.goodTillDate = securityFormValue.orderGoodTilDate;
    } else {
      delete extendedTerms.goodTillDate;
    }

    return extendedTerms;
  }

  isOrderFilledOrHistorical(orderItem: OrderItem): boolean {
    return orderItem !== undefined ?
      (orderItem.orderStatus === OrderStatusType.FILLED || orderItem.orderStatus === OrderStatusType.HISTORY) : false;
  }

  isSimpleOrderForm<T extends OrderEntryChangeFormModel>(securityForm: SimpleChangeOrderForm<T> | ChangeOrderFormContainer<T>):
    securityForm is SimpleChangeOrderForm<T> {
    return securityForm instanceof SimpleChangeOrderForm;
  }

  canExpand(orderItem: OrderItem): boolean {
    return !this.isOrderFilledOrHistorical(orderItem) &&
      (orderItem !== undefined && orderItem.canChange !== undefined ? orderItem.canChange : true);
  }

  private validateOrder(order: Order<OrderTerms>): Observable<OrderCapture<OrderTerms>> {
    return this.orderEntryRestService.validateOrderDetails(order);
  }
}

